import Accordion from "react-bootstrap/Accordion";
import { colors, logSessionHistory } from "../../../../../utils";
import { getSessionDate, getDurationMinutes } from "../../../../../utils/render";
import React, { useState, useEffect, useMemo, useRef } from "react";
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import { Button, Modal, OverlayTrigger, Popover } from "react-bootstrap";
import { Input } from "@bootstrap-styled/v4";
import { Transcript } from "./Transcript";
import styled from "styled-components";
import { useSessionAnalysis } from "../../../../../hooks/useSessionAnalysis/useSessionAnalysis";
import { request } from "../../../../../hooks/useOkoApi/useOkoApi";
import { Summary } from "./Summary";
import { EmptyState } from "components/shared/EmptyState/EmptyState";
import { CloseButton } from "components/shared/CloseButton/CloseButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { logError, LogCategory } from "utils/sentryLogger";
import { trackViewedPage } from "utils/analytics";
import chat from "assets/chat.svg";
import { OngoingSessionContent } from "./OngoingSessionContent";
import { TagLevelSmall } from "components/shared/TagLevelSmall/TagLevelSmall";
import { BadgeXSmall } from "components/shared/Badges/BadgeXSmall";
import { BadgePlaceholder } from "components/shared/Badges/BadgePlaceholder";
import { SessionInProgressTag, AnalyzingSessionTag } from "components/shared/InProgressTag/InProgressTag";
import { sessionCollabLevel } from "utils/teamworkLevel";
import { isShortDuration } from "utils/sessionDuration";
import { SessionScores } from "./SessionScores";
import { SESSION_REPORT_TABS, ResponsiveTabNav } from "./SessionReportTabs";
import { useTaggedQuotes } from "hooks/useTaggedQuotes";
import { criticalIncidents, criticalTagsOnly } from "utils/taggedQuotes";
import { CriticalIncidentsTab } from "./CriticalIncidentsTab";
import { INCIDENTS_TAB_LABEL, SafetyConcernsTag } from "../shared/criticalIncidentsUi";
import warningIcon from "assets/icons/lesson-report/warning.svg";

// Whether the card should still say it is analyzing.
//
// This used to be read off a missing collaboration level, which held while
// every report narrated one. Under the current prompt the level comes from the
// session's own scores, and a session where no question loop qualified for
// scoring has a finished report and no level -- so the card called it
// "analyzing" and would have done so forever.
//
// `report_ready` is the server's own answer and wins whenever it is present.
// The old inference is kept only for a server that predates the field, never
// for an explicit false: a report that genuinely is not finished is exactly
// what the tag is for.
const isAnalyzing = (value) =>
  value?.report_ready != null
    ? !value.report_ready
    : sessionCollabLevel(value) === null;

const QuickLinksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding-top: 1.5rem;
`

const QuickLinksRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const QuickLinksLabel = styled.span`
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 700;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: #002E19;
`

const QuickLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: #002E19;
  text-decoration: underline;
  cursor: pointer;

  &:focus-visible {
    outline: 2px solid ${colors.blue800};
    outline-offset: 2px;
  }
`

const QuickLinkDivider = styled.span`
  font-family: Inter, sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 22px;
  letter-spacing: 0.25px;
  color: #CBCDCC;
`

// Wait for the tab panel to mount/animate in before moving focus into it.
const FOCUS_PANEL_DELAY_MS = 120;
// Let the tab content render before scrolling it into view.
const SCROLL_INTO_VIEW_DELAY_MS = 100;

export const SessionSummary = React.forwardRef(({
  value,
  index,
  durationMs,
  challengeDataErrors,
  setChallengeDataErrors,
  challengeData,
  isChallengeDataLoading,
  fetchChallengeData,
  handleAccordionSelect,
  updateSessionEndedAt,
  autoExpand,
  autoExpandStudentName,
  autoExpandQuote,
  autoExpandQuoteId,
  autoExpandReportTab,
  reportTab,
  educatorId
}, ref) => {
  const [key, setKey] = useState(() => (autoExpand && autoExpandReportTab ? autoExpandReportTab : 'summary'));
  const tabsRef = useRef(null);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [reportDetailsText, setReportDetailsText] = useState('');
  const [isReportLoading, setIsReportLoading] = useState(false);
  // Question picked from the Session Summary tab's Group Results; switches to
  // the Question Detail tab and selects that question.
  const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(null);
  // Quote picked on the Critical Incidents tab; switches to the Transcript tab
  // and highlights that utterance, same as an external deep link would.
  const [incidentQuote, setIncidentQuote] = useState(null);
  const challengeFetchedRef = useRef(false);
  const reportTabPreselectRef = useRef(false);

  useEffect(() => {
    if (isShortDuration(durationMs)) {
      console.log(`Short session detected: ${value.session_id}, duration: ${Math.floor(durationMs / 1000)}s`);
    }
  }, [durationMs, value.session_id]);

  useEffect(() => {
    if (autoExpand) {
      fetchSummary(value.session_id);
      fetchTranscript(value.session_id);
      fetchChallengeData(value.session_id, index);
      if (autoExpandQuote || autoExpandQuoteId) {
        setKey('transcript');
      } else if (autoExpandReportTab) {
        setKey(autoExpandReportTab);
      } else if (autoExpandStudentName) {
        setKey('students');
      }
    }
  }, [autoExpand, autoExpandReportTab]); // eslint-disable-line react-hooks/exhaustive-deps

  const {
    transcriptData,
    summaryData,
    isLoadingTranscript,
    isLoadingSummary,
    transcriptError,
    summaryError,
    fetchTranscript,
    fetchSummary,
  } = useSessionAnalysis();

  const isLoading = isLoadingTranscript || isLoadingSummary;
  const error = transcriptError || summaryError;

  // The transcript and the Critical Incidents tab both read the tagger's
  // flagged quotes; fetch them once the session is opened (the summary
  // arriving is the sign of that) or the teacher is deep-linked into them.
  const taggedQuotes = useTaggedQuotes(
    value.session_id,
    Boolean(summaryData) ||
      key === 'transcript' ||
      key === 'incidents' ||
      Boolean(autoExpandQuote) ||
      Boolean(autoExpandQuoteId) ||
      reportTab === 'safety-concerns' ||
      Boolean(autoExpandReportTab === 'incidents'),
    educatorId,
  );

  // A safety-alert email names its quote by sessiontaggedquote.id rather than
  // by text; resolve it against the session's own tagged quotes once they
  // arrive. No match (id absent from the payload, or simply wrong) leaves the
  // transcript on the tab with nothing highlighted, same as no quote at all.
  const quoteFromId = useMemo(() => {
    if (!autoExpandQuoteId) return null;
    const match = taggedQuotes.find(
      (q) => (q.quoteId != null && String(q.quoteId) === String(autoExpandQuoteId)) ||
             (q.id != null && String(q.id) === String(autoExpandQuoteId)),
    );
    return match ? { timestamp: match.quote_timestamp, text: match.quote } : null;
  }, [autoExpandQuoteId, taggedQuotes]);

  const incidents = useMemo(() => criticalIncidents(taggedQuotes), [taggedQuotes]);
  const hasIncidents = incidents.length > 0;

  // When deep-linked via report_tab=safety-concerns, switch to the incidents tab
  // once taggedQuotes are loaded and incidents exist. Apply once via ref to avoid
  // refiring the effect on every render when the user switches tabs.
  useEffect(() => {
    if (reportTab === 'safety-concerns' && hasIncidents && !reportTabPreselectRef.current) {
      reportTabPreselectRef.current = true;
      setKey('incidents');
    }
  }, [reportTab, hasIncidents]);

  // The session report's tabs: the shared four, with Critical Incidents
  // slotted after Session Summary. Built here rather than added to
  // SESSION_REPORT_TABS because the admin prompt console mounts a panel per
  // entry of that constant and has no incidents panel.
  const showIncidentsTab = hasIncidents || autoExpandReportTab === 'incidents' || reportTab === 'safety-concerns';
  const allTabs = useMemo(
    () =>
      showIncidentsTab
        ? [
            SESSION_REPORT_TABS[0],
            { key: 'incidents', label: INCIDENTS_TAB_LABEL, ...(hasIncidents ? { icon: warningIcon } : {}) },
            ...SESSION_REPORT_TABS.slice(1),
          ]
        : SESSION_REPORT_TABS,
    [showIncidentsTab, hasIncidents],
  );

  // The Group Results (Session Summary tab) needs per-question challenge
  // data, which is otherwise only fetched lazily on the Question Detail tab.
  // Pull it once the report is available.
  useEffect(() => {
    if (
      summaryData &&
      summaryData.report_available !== false &&
      !challengeFetchedRef.current
    ) {
      challengeFetchedRef.current = true;
      fetchChallengeData(value.session_id, index);
    }
  }, [summaryData]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmitDelayedReport = async () => {
    setIsReportLoading(true);
    const defaultMessage = "[Delayed Report] Report is taking longer than expected to generate.";
    const details = reportDetailsText
      ? `${defaultMessage}\n\nAdditional info: ${reportDetailsText}`
      : defaultMessage;

    await request("feedback/submit_custom_feedback", {
      payload: {
        feedback_type: "bug",
        details,
        session_id: value.session_id,
        report_location: "Educator Dashboard"
      },
    }).then(() => {
      setIsReportModalOpen(false);
      setReportDetailsText('');
    }).catch((e) => {
      console.error(e);
      logError("Failed to submit delayed report feedback", e, {
        category: LogCategory.ERROR,
        sessionId: value.session_id,
      });
    }).finally(() => setIsReportLoading(false));
  };

  const handleCloseReportModal = () => {
    if (isReportLoading) return;
    setIsReportModalOpen(false);
  };

  const handleSessionClosed = (endedAt) => {
    updateSessionEndedAt(value.session_id, endedAt);
  };

  const handleSessionSummary = async (sessionId) => {
    logSessionHistory("Session history: Session detail view opened", {
      event: "session_opened",
      sessionId: sessionId,
      groupCode: value.group_code,
      activityType: value.activity_type,
    });
    // Three independent reads; in series the open took the sum of the three.
    // Errors are already handled in useSessionAnalysis.
    fetchChallengeData(value.session_id, index);
    await Promise.allSettled([
      summaryData ? undefined : fetchSummary(sessionId),
      transcriptData ? undefined : fetchTranscript(sessionId),
    ]);
  };

  const focusActivePanel = (k) => {
    setTimeout(() => {
      document.getElementById(`session-tabpanel-${k}`)?.focus({ preventScroll: true });
    }, FOCUS_PANEL_DELAY_MS);
  };

  const switchTab = (k) => {
    setKey(k);
    setTimeout(() => tabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), SCROLL_INTO_VIEW_DELAY_MS);
    focusActivePanel(k);
  };

  const handleSelectQuestion = (questionIndex) => {
    setSelectedQuestionIndex(questionIndex);
    switchTab('questions');
  };

  const handleIncidentQuoteClick = (quote) => {
    setIncidentQuote(quote);
    switchTab('transcript');
  };

  const handleTabSelect = (k) => {
    const tabNames = {
      summary: "Session Summary",
      incidents: INCIDENTS_TAB_LABEL,
      students: "Student Detail",
      questions: "Question Detail",
      transcript: "Transcript",
    };
    logSessionHistory("Session history: Tab switched", {
      event: "tab_switched",
      sessionId: value.session_id,
      tab: k,
      tabName: tabNames[k],
    });
    if (k && k !== key) {
      trackViewedPage("Session Report", {
        tab: tabNames[k] || k,
        group_code: value?.group_code ? value.group_code.toUpperCase() : null,
        session_id: value?.session_id || null,
        session_timestamp: value?.started_at || null,
        standard_id: value?.standard_prefix || value?.standards?.[0]?.standard_code || null,
        // Full addressing set for lesson sessions; standard_id stays scalar
        // for continuity with existing dashboards.
        standard_ids:
          value?.lesson_standard_prefixes?.length > 0
            ? value.lesson_standard_prefixes.map((sp) => sp.standard_prefix)
            : null,
      });
    }
    setKey(k);
    focusActivePanel(k);
  };

  const quickLinks = (currentKey) => (
    <QuickLinksContainer>
      <ReportIssueCtaButton
        onClick={() => setIsReportModalOpen(true)}
        variant="light"
      >
        <img src={chat} alt="chat icon" />
        Report Issue to OKO
      </ReportIssueCtaButton>
      <QuickLinksRow>
        <QuickLinksLabel>Quick links:</QuickLinksLabel>
        {allTabs
          .filter(t => t.key !== currentKey)
          .map((t, i, arr) => (
            <React.Fragment key={t.key}>
              <QuickLink type="button" onClick={() => switchTab(t.key)}>{t.label}</QuickLink>
              {i < arr.length - 1 && <QuickLinkDivider aria-hidden="true">|</QuickLinkDivider>}
            </React.Fragment>
          ))
        }
      </QuickLinksRow>
    </QuickLinksContainer>
  );

  const ongoingSessionContent = (
    <OngoingSessionContent
      sessionId={value.session_id}
      onSessionClosed={handleSessionClosed}
    />
  );

  const delayedProcessingContent = (
    <DelayedProcessingContainer>
      <EmptyState
        message="Turning conversation into insights…"
        description={<>We are busy analyzing the group's math work and collaboration to generate your report. This deep analysis takes about 5 minutes, so go do your thing—we'll handle the rest.<br /><br /><span style={{ fontWeight: 700, fontStyle: 'normal' }}>Still waiting?</span> Don't worry, we got you. Try <span style={{ fontWeight: 700, fontStyle: 'normal' }}>refreshing your browser</span> and if that doesn't work, click <span style={{ fontWeight: 700, fontStyle: 'normal' }}>"Report an Issue"</span> so we can help.</>}
      />
      <ReportIssueButton
        onClick={() => setIsReportModalOpen(true)}
        variant="light"
      >
        <img src={chat} alt="chat icon" />
        Report an Issue to OKO
      </ReportIssueButton>
    </DelayedProcessingContainer>
  );

  return (
    <Container
      ref={ref}
      eventKey={index}
      key={index}
    >
      <Accordion.Header
        onClick={async () => {
          await handleSessionSummary(value.session_id);
        }}
        className={value.ended_at ? "" : "accordion-in-progress"}
      >
        <PillWrapper>
          <ActivitySection>
            <PillTitle>Group Session: {value.group_code.toUpperCase()}</PillTitle>
            <PillMeta>{getSessionDate(value.started_at)}</PillMeta>
            <PillMeta>
              {!value.ended_at ? (
                <SessionInProgressTag>Session in Progress</SessionInProgressTag>
              ) : isAnalyzing(value) ? (
                <AnalyzingSessionTag />
              ) : (
                <>
                  {getDurationMinutes(value.started_at, value.ended_at)}
                  {value.total_questions != null && <><MetaDivider />{value.total_questions} Questions</>}
                </>
              )}
            </PillMeta>
            {(value.incident_counts || []).length > 0 && <SafetyConcernsTag />}
          </ActivitySection>

          {(() => {
            const teamworkLevel = sessionCollabLevel(value);
            const studentCollabLevels = Array.isArray(value.student_collab_levels)
              ? value.student_collab_levels.filter(
                (s) => s && s.student_name && s.student_collab_level >= 1 && s.student_collab_level <= 4
              )
              : [];
            const badgeAndTag = teamworkLevel ? (
              <>
                <BadgeXSmall level={teamworkLevel} />
                <TagLevelSmall level={teamworkLevel} />
              </>
            ) : (
              <BadgePlaceholder />
            );
            const hasPerStudent = studentCollabLevels.length > 0;
            return (
              <CollabSection>
                <SectionLabel>Group Collaboration</SectionLabel>
                {hasPerStudent ? (
                  <OverlayTrigger
                    placement="bottom"
                    trigger={["hover", "focus"]}
                    overlay={
                      <CollabLevelsPopover id={`collab-levels-popover-${value.session_id}`}>
                        <Popover.Body>
                          <CollabPopoverHeader>Student Collaboration</CollabPopoverHeader>
                          <CollabLevelsGrid $rows={Math.ceil(studentCollabLevels.length / 2)}>
                            {[...studentCollabLevels]
                              .sort((a, b) => {
                                if (b.student_collab_level !== a.student_collab_level) {
                                  return b.student_collab_level - a.student_collab_level;
                                }
                                return a.student_name.localeCompare(b.student_name);
                              })
                              .map((s) => (
                                <StudentRow key={s.student_id || s.student_name}>
                                  <StudentName>{s.student_name}</StudentName>
                                  <TagLevelSmall level={s.student_collab_level} />
                                </StudentRow>
                              ))}
                          </CollabLevelsGrid>
                        </Popover.Body>
                      </CollabLevelsPopover>
                    }
                  >
                    <CollabBadgeHover>{badgeAndTag}</CollabBadgeHover>
                  </OverlayTrigger>
                ) : (
                  badgeAndTag
                )}
              </CollabSection>
            );
          })()}

          <StudentsSection>
            <SessionScores value={value} educatorId={educatorId} />
          </StudentsSection>

        </PillWrapper>
      </Accordion.Header>

      <Accordion.Body>
        <div ref={tabsRef} />
        <div>
          {/* react-bootstrap generates its own pane ids and overrides the one
              a Tab.Pane declares, so the tabs' aria-controls pointed at
              nothing and the focus move on tab switch silently did nothing.
              Generating the ids the nav already advertises joins them up. */}
          <Tab.Container
            id="controlled-tab"
            activeKey={key}
            onSelect={handleTabSelect}
            generateChildId={(eventKey, type) =>
              `session-${type === "tabpane" ? "tabpanel" : "tab"}-${eventKey}`
            }
          >
            <ResponsiveTabNav activeKey={key} onSelect={handleTabSelect} tabs={allTabs} />
            <Tab.Content>
              <Tab.Pane eventKey="summary" role="tabpanel" id="session-tabpanel-summary" aria-labelledby="session-tab-summary" tabIndex={0}>
                {isLoadingSummary ? (
                  <LoadingContainer>
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    <LoadingText>Loading summary...</LoadingText>
                  </LoadingContainer>
                ) : summaryData && summaryData?.report_available !== false ? (
                  <Summary
                    summaryData={summaryData}
                    isShortSession={isShortDuration(durationMs)}
                    value={value}
                    challengeData={challengeData}
                    onSelectQuestion={handleSelectQuestion}
                    onViewIncidents={() => switchTab('incidents')}
                  />
                ) : summaryData?.report_available === false ? (
                  <EmptyState
                    message="Insights Unavailable for this Session"
                    description="To ensure you can trust the data, OKO verifies that every student quote in our reports accurately matches the session transcript. For this session, we could not verify the quotes with 100% certainty, so we have suppressed the report. We are actively releasing updates to report analysis to minimize issues like this in the future. You can still review the Session Transcript above."
                  />
                ) : !value.ended_at ? (
                  ongoingSessionContent
                ) : (
                  delayedProcessingContent
                )}
                {quickLinks('summary')}
              </Tab.Pane>

              <Tab.Pane eventKey="incidents" role="tabpanel" id="session-tabpanel-incidents" aria-labelledby="session-tab-incidents" tabIndex={0}>
                <CriticalIncidentsTab
                  taggedQuotes={taggedQuotes}
                  onQuoteClick={handleIncidentQuoteClick}
                />
                {quickLinks('incidents')}
              </Tab.Pane>

              <Tab.Pane eventKey="students" role="tabpanel" id="session-tabpanel-students" aria-labelledby="session-tab-students" tabIndex={0}>
                {isLoadingSummary ? (
                  <LoadingContainer>
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    <LoadingText>Loading students...</LoadingText>
                  </LoadingContainer>
                ) : summaryData && summaryData?.report_available !== false ? (
                  <Summary
                    summaryData={summaryData}
                    studentsOnly
                    initialStudentName={autoExpandStudentName}
                    isShortSession={isShortDuration(durationMs)}
                    value={value}
                    challengeData={challengeData}
                  />
                ) : summaryData?.report_available === false ? (
                  <EmptyState
                    message="Insights Unavailable for this Session"
                    description="To ensure you can trust the data, OKO verifies that every student quote in our reports accurately matches the session transcript. For this session, we could not verify the quotes with 100% certainty, so we have suppressed the report. We are actively releasing updates to report analysis to minimize issues like this in the future. You can still review the Session Transcript above."
                  />
                ) : !value.ended_at ? (
                  ongoingSessionContent
                ) : (
                  delayedProcessingContent
                )}
                {quickLinks('students')}
              </Tab.Pane>

              <Tab.Pane eventKey="questions" role="tabpanel" id="session-tabpanel-questions" aria-labelledby="session-tab-questions" tabIndex={0}>
                {isLoadingSummary ? (
                  <LoadingContainer>
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    <LoadingText>Loading questions...</LoadingText>
                  </LoadingContainer>
                ) : summaryData && summaryData?.report_available !== false ? (
                  <Summary
                    value={value}
                    index={index}
                    challengeDataErrors={challengeDataErrors}
                    setChallengeDataErrors={setChallengeDataErrors}
                    challengeData={challengeData}
                    isChallengeDataLoading={isChallengeDataLoading}
                    fetchChallengeData={fetchChallengeData}
                    summaryData={summaryData}
                    initialQuestionIndex={selectedQuestionIndex}
                    questionsOnly
                  />
                ) : summaryData?.report_available === false ? (
                  <EmptyState
                    message="Insights Unavailable for this Session"
                    description="To ensure you can trust the data, OKO verifies that every student quote in our reports accurately matches the session transcript. For this session, we could not verify the quotes with 100% certainty, so we have suppressed the report. We are actively releasing updates to report analysis to minimize issues like this in the future. You can still review the Session Transcript above."
                  />
                ) : !value.ended_at ? (
                  ongoingSessionContent
                ) : (
                  delayedProcessingContent
                )}
                {quickLinks('questions')}
              </Tab.Pane>

              <Tab.Pane eventKey="transcript" role="tabpanel" id="session-tabpanel-transcript" aria-labelledby="session-tab-transcript" tabIndex={0}>
                {isLoadingTranscript ? (
                  <LoadingContainer>
                    <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                    <LoadingText>Loading transcript...</LoadingText>
                  </LoadingContainer>
                ) : transcriptData ? (
                  <Transcript transcript={transcriptData} challengeData={challengeData} error={error} isLoading={isLoading} speechTags={criticalTagsOnly(taggedQuotes)} highlightQuote={incidentQuote || quoteFromId || autoExpandQuote} />
                ) : !value.ended_at ? (
                  ongoingSessionContent
                ) : (
                  delayedProcessingContent
                )}
                {quickLinks('transcript')}
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      </Accordion.Body>

      <ReportModalContainer show={isReportModalOpen} onHide={handleCloseReportModal}>
        {isReportLoading && (
          <ReportModalSpinner>
            <FontAwesomeIcon icon={faSpinner} spin size="5x" />
          </ReportModalSpinner>
        )}
        <ReportModalHeader>
          <CloseButton
            onClick={handleCloseReportModal}
            color={'#000'}
            width={24}
            height={24}
            customCss={`position: initial;`}
          />
          <span style={{ fontSize: "20px", fontWeight: 700, textAlign: 'center', width: '100%' }}>
            Report an Issue
          </span>
        </ReportModalHeader>
        <ReportModalBody>
          <ReportModalDefaultText>
            This report will include information that the session analysis is taking longer than expected.
          </ReportModalDefaultText>
          <Input
            placeholder="Add any additional details here (optional)..."
            maxLength={500}
            type="textarea"
            style={{ height: "100px", padding: "0.75rem 1rem", minHeight: '150px', fontWeight: 300, fontSize: '0.875rem' }}
            onChange={(e) => setReportDetailsText(e.target.value)}
            value={reportDetailsText}
          />
        </ReportModalBody>
        <ReportModalFooter>
          <Button
            onClick={handleSubmitDelayedReport}
            style={{
              backgroundColor: '#605DEC',
              border: "none",
              fontSize: "0.875rem",
              padding: "0.5rem 1rem",
              cursor: isReportLoading ? "not-allowed" : "pointer",
              opacity: isReportLoading ? 0.5 : 1,
              margin: 0,
            }}
            disabled={isReportLoading}
          >
            Submit Report
          </Button>
        </ReportModalFooter>
      </ReportModalContainer>
    </Container>
  );
});

export const Container = styled(Accordion.Item)`
  background-color: ${colors.neutral100};
  border: none;
  border-radius: 12px;
  padding: 1rem 0;
  box-shadow: 0 1px 2px 0 rgba(14, 26, 49, 0.05);

  .accordion-button::after {
    background-position: center;
  }

  .accordion-button:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &:has(.accordion-button:focus-visible) {
    outline: 2px solid ${colors.blue800};
    outline-offset: 2px;
  }

  &:has(.accordion-collapse.show),
  &:has(.accordion-collapse.collapsing) {
    background: #FFF;
    border: 1px solid #000;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(14, 26, 49, 0.10), 0 1px 2px 0 rgba(14, 26, 49, 0.06);
    overflow: hidden;
  }

  &:has(.accordion-collapse.show) .accordion-button,
  &:has(.accordion-collapse.collapsing) .accordion-button {
    border-radius: 12px 12px 0 0;
    background: #FEFEFD;
    box-shadow: 0 1px 2px 0 rgba(14, 26, 49, 0.05);
    padding-bottom: 1rem;
  }

  &:has(.accordion-collapse.show) .accordion-body,
  &:has(.accordion-collapse.collapsing) .accordion-body {
    border-top: 1px solid #EEECE7;
    padding-top: 0;
  }

  .tab-content {
    padding: 0 1.5rem .5rem;
  }
`

const PillWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: stretch;
  padding: 0 4rem 0 1.5rem;
  /* 1.5rem (24px) keeps each section divider 24px from neighbours, matching
     the live session pill (LiveSessionPill Divider margin: 0 24px). */
  gap: 1.5rem;
  flex: 1;
  min-width: 0;
  /* Laptop (≤1024px, within Figma's 840–1199dp range): wrap so the title +
     Group Collaboration stay on the top row (vertical divider between them)
     and Student Proficiency drops to a full-width second row below a
     horizontal divider. row-gap is the Figma "Content" 16px stack gap; the
     column-gap (24px) stays for the top-row divider (Figma 2543:57749 / 58111). */
  @media (max-width: 1024px) {
    flex-wrap: wrap;
    row-gap: 16px;
  }
`

const ActivitySection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
  gap: 4px;
  width: 215px;
  flex-shrink: 0;
`

const PillTitle = styled.p`
  font-size: 18px;
  font-weight: 700;
  line-height: 26px;
  margin: 0;
`

const PillMeta = styled.p`
  font-size: 14px;
  color: #000;
  margin: 0;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 8px;
`

const MetaDivider = styled.span`
  display: inline-block;
  width: 1px;
  height: 1em;
  background-color: ${colors.green300};
`

const SectionLabel = styled.p`
  font-size: 14px;
  font-weight: 700;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
`


const CollabSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding-left: 1.5rem;
  border-left: 1px solid #FFF;
`

const CollabBadgeHover = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`

const CollabLevelsPopover = styled(Popover)`
  max-width: none;
  width: max-content;
  background-color: #FFF;
  border: 1px solid #EEECE7;
  border-radius: 12px;
  box-shadow: 0 12px 32px 0 rgba(14, 26, 49, 0.12);

  .popover-arrow::before,
  .popover-arrow::after {
    display: none;
  }

  .popover-body {
    padding: 16px;
    width: max-content;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`

const CollabPopoverHeader = styled.p`
  margin: 0;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 700;
  color: #00140B;
`

const CollabLevelsGrid = styled.div`
  display: grid;
  grid-template-rows: repeat(${({ $rows }) => $rows}, auto);
  grid-auto-flow: column;
  gap: 6px 24px;
`

const StudentsSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: 1 0 0;
  padding-left: 1.5rem;
  border-left: 1px solid #FFF;
  min-width: 0;
  /* Laptop (≤1024px): full-width second row — the vertical divider becomes a
     horizontal rule above the section, with the Figma 16px gap below it. */
  @media (max-width: 1024px) {
    flex-basis: 100%;
    border-left: none;
    border-top: 1px solid #FFF;
    padding-left: 0;
    padding-top: 16px;
  }
`

const StudentRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`

const StudentName = styled.span`
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`


export const TabContainer = styled(React.forwardRef((props, ref) => (
  <Tabs {...props} ref={ref} />
)))`
  border: none;
  position: relative;
  gap: 3rem;

  .nav-tabs {
    position: relative;
    border: none;
    overflow: visible;
    display: flex;
    padding: 0 1.5rem;
  }

  .nav-link {
    color: #002E19;
    border: none;
    padding: 0;
    transition: all 0.2s ease;
    background: none;
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 22px;
    letter-spacing: 0.25px;
    position: relative;
    flex: 1;
    text-align: center;
  }

  .nav-link:hover {
    color: inherit;
    border: none;
    background-color: transparent;
  }

  .nav-link.active {
    border: none;
    color: #605DEC;
    background: transparent;
  }

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    height: 3px;
    background-color: #605DEC;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    z-index: 10;
    width: 0;
  }
  
  &[data-active-key="summary"]::before {
    width: 143px;
    left: 24px;
    transform: translateX(0);
  }

  &[data-active-key="students"]::before {
    width: 111px;
    left: 104px;
    transform: translateX(100%);
  }

  &[data-active-key="questions"]::before {
    width: 121px;
    left: 375px;
    transform: translateX(0);
  }

  &[data-active-key="transcript"]::before {
    width: 79px;
    left: 543px;
    transform: translateX(0);
  }

`

export const TabContent = styled(Tab)`
  color: inherit;
  border: none;
  text-align: center;
  padding: 0;
`

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  gap: 1rem;
  color: ${colors.nightlyWoods500};
`

const LoadingText = styled.p`
  font-size: 1rem;
  font-weight: 500;
  margin: 0;
`

const DelayedProcessingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`

const ReportIssueButton = styled(Button)`
  display: flex;
  gap: 8px;
  align-items: center;
  background-color: transparent;
  color: #002E19;
  border: none;
  font-weight: 600;

  &:hover {
    background-color: ${colors.neutral200};
    color: #002E19;
  }
`

const ReportIssueCtaButton = styled(Button)`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 200px;
  background-color: transparent;
  color: #002E19;
  border: none;
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 24px;

  &:hover, &:active {
    background-color: #F2F4F7;
    color: #002E19;
  }
`

const ReportModalContainer = styled(Modal)`
  width: 100%;

  > div {
    max-width: 388px;

    .modal-content {
      border-radius: 1rem;
    }
  }
`

const ReportModalSpinner = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
`

const ReportModalHeader = styled(Modal.Header)`
  display: flex;
  flex-direction: column;
  padding: 1rem 2rem 0;
  gap: 1rem;
  align-items: flex-end;
  justify-content: flex-end;
  height: 100px;
  border: none;
  margin: 0;
`

const ReportModalBody = styled(Modal.Body)`
  padding: 1rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const ReportModalDefaultText = styled.p`
  font-size: 0.875rem;
  color: ${colors.nightlyWoods700};
  margin: 0;
  text-align: center;
  font-style: italic;
`

const ReportModalFooter = styled(Modal.Footer)`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  padding-top: 0;
  padding-bottom: 1rem;
`

